import { EXPORT_COLORS } from '@consigli/types';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import AecomLogoPng from '@/assets/images/aecom-logo-black.png';

import { toUTC } from '../format-meeting-datetime';
import { PDF_CONFIG } from '../utils/export-pdf-config';
import { buildColumnStyles } from '../utils/export-pdf-utils';
import {
  toTitleCase,
  stripDateFromTitle,
  getExportDateSuffix,
  sanitizeFilename,
} from '../utils/export-types';
import { formatResponsibleParties } from '../utils/participant-utils';
import { ensurePdfFont } from '../utils/pdf-fonts';
import {
  cleanSummaryText,
  stripSpeakerPrefix,
  extractDiscussionStructure,
  extractSummaryIntro,
} from '../utils/text-utils';

import type { ExportData } from '../utils/export-types';

export type ExportPdfData = ExportData;

// Extract colors for easier use (using rgb format for jsPDF)
const BLACK = EXPORT_COLORS.black.rgb;
const HEADER_GREY = EXPORT_COLORS.headerGrey.rgb;
const TEXT_GREY = EXPORT_COLORS.textGrey.rgb;

/**
 * Normalize all text fields in table rows to remove inter-character spacing
 * and other encoding artifacts that can appear in AI-generated summaries.
 */

export const useExportPdf = () => {
  const { t, i18n } = useTranslation();
  const exportToPdf = useCallback(
    async (data: ExportPdfData) => {
      const { meeting, transcripts, summary, actionItems, agreements } = data;

      const doc = new jsPDF({
        orientation: PDF_CONFIG.page.orientation,
        unit: PDF_CONFIG.page.unit,
        format: PDF_CONFIG.page.format,
      });

      // ===== Embed a Unicode font that matches the document's language =====
      // jsPDF's built-in fonts only cover Latin-1, so CJK/Vietnamese text would
      // render as mojibake. The data has no language field, so we detect the
      // script from the actual content and embed the matching Noto font (fetched
      // on demand). `fontFamily` then replaces 'helvetica' for every piece of text.
      // Build font detection sample. Put the translated UI labels FIRST so they
      // are never cut off by the 2000-char cap — if the app language is Japanese
      // the labels contain Kana which triggers NotoSansJP even when all meeting
      // content is Latin-script English.
      const detectionSample = [
        t('meeting-secretary.exports.summary'),
        t('meeting-secretary.exports.action-items'),
        t('meeting-secretary.exports.agreements'),
        t('meeting-secretary.exports.transcript'),
        t('meeting-secretary.exports.headers.date'),
        t('meeting-secretary.exports.headers.organizer'),
        meeting.title,
        summary?.generatedSummaryText ?? '',
        ...actionItems.map((i) => `${i.descriptionText || ''} ${i.responsiblePerson?.name || ''}`),
        ...agreements.map(
          (a) =>
            `${a.agreement || ''} ${formatResponsibleParties(a.responsibleParties)} ${a.sourceTextFromTranscript || ''}`,
        ),
        ...transcripts.map((tr) => `${tr.speaker || ''} ${tr.text || ''}`),
      ]
        .join(' ')
        .slice(0, 2000);
      const fontFamily = await ensurePdfFont(doc, detectionSample);

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = PDF_CONFIG.page.margin;
      const usableWidth = pageWidth - margin * 2;
      let yPos = margin;

      // Shared table styling — keeps every table visually identical and sourced
      // from PDF_CONFIG so there are no per-table hard-coded style values.
      const tableTheme = (headFontSize: number, bodyFontSize: number) => ({
        headStyles: {
          fillColor: HEADER_GREY,
          textColor: BLACK,
          fontStyle: 'bold' as const,
          fontSize: headFontSize,
        },
        bodyStyles: {
          fontSize: bodyFontSize,
          textColor: BLACK,
          overflow: 'linebreak' as const,
          valign: 'top' as const,
        },
        styles: {
          font: fontFamily,
          cellPadding: PDF_CONFIG.table.cellPadding,
          lineColor: PDF_CONFIG.table.lineColor,
          lineWidth: PDF_CONFIG.table.lineWidth,
          halign: 'left' as const,
        },
        margin: { left: margin, right: margin },
      });

      // Calculate meeting info
      const startDate = new Date(toUTC(meeting.scheduledStartTime));
      const endDate = new Date(toUTC(meeting.scheduledEndTime));
      const durationMinutes = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60));

      const dateStr = startDate.toLocaleDateString(i18n.language, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });

      const timeStr = startDate.toLocaleTimeString(i18n.language, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });

      // Helper to add a new page if needed
      const checkPageBreak = (neededSpace: number) => {
        if (yPos + neededSpace > pageHeight - margin) {
          doc.addPage();
          yPos = margin;
        }
      };

      // ===== Header (AECOM Logo top right, title on left) =====
      const logoWidth = 35;
      const logoHeight = 13.5;
      doc.addImage(
        AecomLogoPng,
        'PNG',
        pageWidth - margin - logoWidth,
        margin,
        logoWidth,
        logoHeight,
      );

      // Meeting title (LEFT side, at top) - Title Case, black (strip date if present since it's shown below)
      doc.setTextColor(...BLACK);
      doc.setFontSize(PDF_CONFIG.font.title);
      doc.setFont(fontFamily, 'bold');
      doc.text(toTitleCase(stripDateFromTitle(meeting.title)), margin, margin + 5);

      // Meeting metadata line (grey text, below title)
      const meetingInfo = `${t('meeting-secretary.exports.headers.date')}: ${dateStr}  |  ${t('meeting-secretary.exports.headers.time')}: ${timeStr} (${durationMinutes} min)  |  ${t('meeting-secretary.exports.headers.organizer')}: ${meeting.organizerEmail ?? ''}`;
      doc.setTextColor(...TEXT_GREY);
      doc.setFontSize(PDF_CONFIG.font.metadata);
      doc.setFont(fontFamily, 'normal');
      doc.text(meetingInfo, margin, margin + 12);

      doc.setTextColor(0, 0, 0);
      yPos = margin + 25;

      // Helper to add a section header (matches DOCX style)
      const addSectionHeader = (title: string) => {
        checkPageBreak(15);
        doc.setTextColor(...BLACK);
        doc.setFontSize(PDF_CONFIG.font.sectionHeader);
        doc.setFont(fontFamily, 'bold');
        doc.text(title, margin, yPos);

        // Add underline (matches DOCX border bottom)
        yPos += 2;
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.3);
        doc.line(margin, yPos, pageWidth - margin, yPos);

        doc.setTextColor(0, 0, 0);
        yPos += 6;
      };

      // ===== Summary Section =====
      if (summary?.generatedSummaryText) {
        addSectionHeader(t('meeting-secretary.exports.summary'));

        doc.setTextColor(...BLACK);
        doc.setFontSize(PDF_CONFIG.font.body);
        doc.setFont(fontFamily, 'normal');
        // Structured "Key Discussion Points" breakdown (numbered topics), if present.
        const discussionStructure = extractDiscussionStructure(summary.generatedSummaryText);

        // When a structured breakdown exists, render only the intro prose here so
        // the topic bullets aren't duplicated below; otherwise render the full
        // cleaned summary.
        const summaryText =
          discussionStructure.length > 0
            ? extractSummaryIntro(summary.generatedSummaryText)
            : cleanSummaryText(summary.generatedSummaryText);

        // Render main summary text
        if (summaryText.trim()) {
          const summaryLines = doc.splitTextToSize(summaryText.trim(), usableWidth);
          summaryLines.forEach((line: string) => {
            checkPageBreak(PDF_CONFIG.spacing.bodyLineHeight);
            doc.text(line, margin, yPos);
            yPos += PDF_CONFIG.spacing.bodyLineHeight;
          });
          yPos += PDF_CONFIG.spacing.afterSummary;
        }

        // Render the discussion structure
        if (discussionStructure.length > 0) {
          // Add "Key Discussion Points" header
          checkPageBreak(PDF_CONFIG.spacing.bodyLineHeight);
          doc.setFont(fontFamily, 'bold');
          doc.setFontSize(PDF_CONFIG.font.body);
          doc.text(t('meeting-secretary.exports.key-discussion-points'), margin, yPos);
          yPos += PDF_CONFIG.spacing.bodyLineHeight;
          doc.setFont(fontFamily, 'normal');

          // Render each discussion section
          discussionStructure.forEach((section) => {
            // Render section header as bold
            checkPageBreak(PDF_CONFIG.spacing.bodyLineHeight);
            doc.setFont(fontFamily, 'bold');
            doc.setFontSize(PDF_CONFIG.font.body);
            doc.text(section.header, margin + 5, yPos);
            yPos += PDF_CONFIG.spacing.bodyLineHeight;
            doc.setFont(fontFamily, 'normal');

            // Render bullet points for this section
            section.points.forEach((point) => {
              const pointLines = doc.splitTextToSize(`• ${point}`, usableWidth - 10);
              pointLines.forEach((line: string) => {
                checkPageBreak(PDF_CONFIG.spacing.bodyLineHeight);
                doc.text(line, margin + 10, yPos);
                yPos += PDF_CONFIG.spacing.bodyLineHeight;
              });
            });

            yPos += PDF_CONFIG.spacing.afterSummary;
          });
        }
      }

      // ===== Action Items Table =====
      if (actionItems.length > 0) {
        addSectionHeader(t('meeting-secretary.exports.action-items'));

        autoTable(doc, {
          startY: yPos,
          head: [
            [
              t('meeting-secretary.exports.headers.id'),
              t('meeting-secretary.exports.headers.action'),
              t('meeting-secretary.exports.headers.responsible'),
            ],
          ],
          body: actionItems.map((item, index) => [
            String(index + 1),
            item.descriptionText || '',
            item.responsiblePerson?.name || '',
          ]),
          ...tableTheme(PDF_CONFIG.font.tableHead, PDF_CONFIG.font.tableBody),
          columnStyles: buildColumnStyles(PDF_CONFIG.columnProportions.actionItems, usableWidth, {
            0: { halign: 'center' },
          }),
        });

        yPos =
          (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY +
          PDF_CONFIG.spacing.afterSection;
      }

      // ===== Agreements Table =====
      if (agreements.length > 0) {
        addSectionHeader(t('meeting-secretary.exports.agreements'));

        autoTable(doc, {
          startY: yPos,
          head: [
            [
              t('meeting-secretary.exports.headers.id'),
              t('meeting-secretary.exports.headers.agreement'),
              t('meeting-secretary.exports.headers.responsible'),
              t('meeting-secretary.exports.headers.details'),
            ],
          ],
          body: agreements.map((agreement, index) => [
            String(index + 1),
            agreement.agreement || '',
            formatResponsibleParties(agreement.responsibleParties),
            stripSpeakerPrefix(
              agreement.sourceTextFromTranscript,
              formatResponsibleParties(agreement.responsibleParties),
            ),
          ]),
          ...tableTheme(PDF_CONFIG.font.tableHead, PDF_CONFIG.font.tableBody),
          columnStyles: buildColumnStyles(PDF_CONFIG.columnProportions.agreements, usableWidth, {
            0: { halign: 'center' },
          }),
        });

        yPos =
          (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY +
          PDF_CONFIG.spacing.afterSection;
      }

      // ===== Transcript Table =====
      if (transcripts.length > 0) {
        addSectionHeader(t('meeting-secretary.exports.transcript'));

        autoTable(doc, {
          startY: yPos,
          head: [
            [
              t('meeting-secretary.exports.headers.id'),
              t('meeting-secretary.exports.headers.timestamp'),
              t('meeting-secretary.exports.headers.speaker'),
              t('meeting-secretary.exports.headers.text'),
            ],
          ],
          body: transcripts.map((entry, index) => [
            String(index + 1),
            entry.startTime,
            entry.speaker,
            entry.text,
          ]),
          ...tableTheme(PDF_CONFIG.font.tableHead, PDF_CONFIG.font.tableBody),
          columnStyles: buildColumnStyles(PDF_CONFIG.columnProportions.transcript, usableWidth, {
            0: { halign: 'center' },
          }),
        });
      }

      // Save the PDF - sanitize filename so non-Latin titles export correctly
      const titleCaseFilename = toTitleCase(stripDateFromTitle(meeting.title));
      const sanitizedFilename = sanitizeFilename(titleCaseFilename);
      const dateSuffix = getExportDateSuffix(meeting.title, meeting.scheduledStartTime);
      const filename = `${sanitizedFilename}${dateSuffix}.pdf`;
      doc.save(filename);
    },
    [t, i18n.language],
  );

  return { exportToPdf };
};
